"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isEudbLocation = exports.parseTokenCredential = exports.base64decode = exports.toTelemetrySender = exports.toLogProvider = exports.toMessageHandler = void 0;
exports.generateUuid = generateUuid;
const uuid_1 = require("uuid");
const core_util_1 = require("@azure/core-util");
const constants_1 = require("./constants");
const eventIds = new Map([
    ["chatMessageReceived", 200],
    ["typingIndicatorReceived", 245],
    ["readReceiptReceived", 246],
    ["chatMessageEdited", 247],
    ["chatMessageDeleted", 248],
    ["chatThreadCreated", 257],
    ["chatThreadPropertiesUpdated", 258],
    ["chatThreadDeleted", 259],
    ["participantsAdded", 260],
    ["participantsRemoved", 261],
]);
const publicTeamsUserPrefix = "8:orgid:";
const dodTeamsUserPrefix = "8:dod:";
const gcchTeamsUserPrefix = "8:gcch:";
const teamsVisitorUserPrefix = "8:teamsvisitor:";
const phoneNumberPrefix = "4:";
const acsUserPrefix = "8:acs:";
const acsGcchUserPrefix = "8:gcch-acs:";
const acsDodUserPrefix = "8:dod-acs:";
const spoolUserPrefix = "8:spool:";
const toMessageHandler = (event, listener, resourceEndpoint, gatewayApiVersion) => {
    const eventId = eventIds.get(event);
    return {
        handleMessage(message) {
            let genericPayload = null;
            if (message === null || message === void 0 ? void 0 : message.rawBody) {
                genericPayload = JSON.parse(message.rawBody);
            }
            if (genericPayload === null || genericPayload.eventId !== eventId) {
                return undefined;
            }
            const eventPayload = toEventPayload(event, genericPayload, resourceEndpoint, gatewayApiVersion);
            if (eventPayload === null) {
                return undefined;
            }
            listener(eventPayload);
            return { isHandled: true, resultCode: 200 };
        },
    };
};
exports.toMessageHandler = toMessageHandler;
const toEventPayload = (event, genericPayload, resourceEndpoint, gatewayApiVersion) => {
    if (event === "chatMessageReceived") {
        const payload = genericPayload;
        const eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            id: payload.messageId,
            createdOn: new Date(payload.originalArrivalTime),
            version: payload.version,
            type: payload.messageType,
            message: payload.messageBody,
            metadata: parseJsonString(payload.acsChatMessageMetadata) || {},
            attachments: transformEndpoint(parseJsonString(payload.attachments) || [], resourceEndpoint, gatewayApiVersion),
        };
        return eventPayload;
    }
    if (event === "chatMessageEdited") {
        const payload = genericPayload;
        const eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            id: payload.messageId,
            createdOn: new Date(payload.originalArrivalTime),
            version: payload.version,
            message: payload.messageBody,
            editedOn: new Date(payload.edittime),
            type: payload.messageType,
            metadata: parseJsonString(payload.acsChatMessageMetadata) || {},
            attachments: transformEndpoint(parseJsonString(payload.attachments) || [], resourceEndpoint, gatewayApiVersion),
        };
        return eventPayload;
    }
    if (event === "chatMessageDeleted") {
        const payload = genericPayload;
        const eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            id: payload.messageId,
            createdOn: new Date(payload.originalArrivalTime),
            version: payload.version,
            deletedOn: new Date(payload.deletetime),
            type: payload.messageType,
        };
        return eventPayload;
    }
    if (event === "typingIndicatorReceived") {
        const payload = genericPayload;
        const eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            version: payload.version,
            receivedOn: new Date(payload.originalArrivalTime),
        };
        return eventPayload;
    }
    if (event === "readReceiptReceived") {
        const payload = genericPayload;
        const readReceiptMessageBody = JSON.parse(payload.messageBody);
        const consumptionHorizon = readReceiptMessageBody.consumptionhorizon.split(";");
        const eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: "",
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            chatMessageId: payload.messageId,
            readOn: new Date(+consumptionHorizon[1]),
        };
        return eventPayload;
    }
    if (event === "chatThreadCreated") {
        const payload = genericPayload;
        const createdByPayload = JSON.parse(unescape(payload.createdBy));
        const membersPayload = JSON.parse(unescape(payload.members));
        const propertiesPayload = JSON.parse(unescape(payload.properties));
        const createdBy = {
            id: constructIdentifierKindFromMri(createdByPayload.participantId),
            displayName: createdByPayload.displayName,
        };
        const chatParticipants = membersPayload.map((m) => {
            return {
                id: constructIdentifierKindFromMri(m.participantId),
                displayName: m.displayName,
            };
        });
        const eventPayload = {
            threadId: payload.threadId,
            createdOn: new Date(payload.createTime),
            createdBy: createdBy,
            version: payload.version,
            participants: chatParticipants,
            properties: propertiesPayload,
        };
        return eventPayload;
    }
    if (event === "chatThreadPropertiesUpdated") {
        const payload = genericPayload;
        const updatedByPayload = JSON.parse(unescape(payload.editedBy));
        const propertiesPayload = JSON.parse(unescape(payload.properties));
        const updatedBy = {
            id: constructIdentifierKindFromMri(updatedByPayload.participantId),
            displayName: updatedByPayload.displayName,
        };
        const eventPayload = {
            threadId: payload.threadId,
            updatedOn: new Date(payload.editTime),
            updatedBy: updatedBy,
            version: payload.version,
            properties: propertiesPayload,
        };
        return eventPayload;
    }
    if (event === "chatThreadDeleted") {
        const payload = genericPayload;
        const deletedByPayload = JSON.parse(unescape(payload.deletedBy));
        const deletedBy = {
            id: constructIdentifierKindFromMri(deletedByPayload.participantId),
            displayName: deletedByPayload.displayName,
        };
        const eventPayload = {
            threadId: payload.threadId,
            deletedOn: new Date(payload.deleteTime),
            deletedBy: deletedBy,
            version: payload.version,
        };
        return eventPayload;
    }
    if (event === "participantsAdded") {
        const payload = genericPayload;
        const addedByPayload = JSON.parse(unescape(payload.addedBy));
        const participantsAddedPayload = JSON.parse(unescape(payload.participantsAdded));
        const addedBy = {
            id: constructIdentifierKindFromMri(addedByPayload.participantId),
            displayName: addedByPayload.displayName,
        };
        const chatParticipants = participantsAddedPayload.map((m) => {
            return {
                id: constructIdentifierKindFromMri(m.participantId),
                displayName: m.displayName,
                shareHistoryTime: new Date(m.shareHistoryTime),
            };
        });
        const eventPayload = {
            threadId: payload.threadId,
            addedOn: new Date(payload.time),
            addedBy: addedBy,
            version: payload.version,
            participantsAdded: chatParticipants,
        };
        return eventPayload;
    }
    if (event === "participantsRemoved") {
        const payload = genericPayload;
        const removedByPayload = JSON.parse(unescape(payload.removedBy));
        const participantsRemovedPayload = JSON.parse(unescape(payload.participantsRemoved));
        const removedBy = {
            id: constructIdentifierKindFromMri(removedByPayload.participantId),
            displayName: removedByPayload.displayName,
        };
        const chatParticipants = participantsRemovedPayload.map((m) => {
            return {
                id: constructIdentifierKindFromMri(m.participantId),
                displayName: m.displayName,
                shareHistoryTime: new Date(m.shareHistoryTime),
            };
        });
        const eventPayload = {
            threadId: payload.threadId,
            removedOn: new Date(payload.time),
            removedBy: removedBy,
            version: payload.version,
            participantsRemoved: chatParticipants,
        };
        return eventPayload;
    }
    return null;
};
const toLogProvider = (logger) => {
    return {
        log: (...message) => logger.info(message),
        warn: (...message) => logger.warning(message),
        error: (...message) => logger.error(message),
        debug: (...message) => logger.verbose(message),
        info: (...message) => logger.verbose(message),
    };
};
exports.toLogProvider = toLogProvider;
const toTelemetrySender = (logger) => {
    return {
        logEvent: (clientEvent) => logger.info(clientEvent),
    };
};
exports.toTelemetrySender = toTelemetrySender;
const constructIdentifierKindFromMri = (mri) => {
    if (mri.startsWith(publicTeamsUserPrefix)) {
        return {
            kind: "microsoftTeamsUser",
            rawId: mri,
            microsoftTeamsUserId: mri.substring(publicTeamsUserPrefix.length),
            isAnonymous: false,
            cloud: "public",
        };
    }
    else if (mri.startsWith(dodTeamsUserPrefix)) {
        return {
            kind: "microsoftTeamsUser",
            rawId: mri,
            microsoftTeamsUserId: mri.substring(dodTeamsUserPrefix.length),
            isAnonymous: false,
            cloud: "dod",
        };
    }
    else if (mri.startsWith(gcchTeamsUserPrefix)) {
        return {
            kind: "microsoftTeamsUser",
            rawId: mri,
            microsoftTeamsUserId: mri.substring(gcchTeamsUserPrefix.length),
            isAnonymous: false,
            cloud: "gcch",
        };
    }
    else if (mri.startsWith(teamsVisitorUserPrefix)) {
        return {
            kind: "microsoftTeamsUser",
            rawId: mri,
            microsoftTeamsUserId: mri.substring(teamsVisitorUserPrefix.length),
            isAnonymous: true,
        };
    }
    else if (mri.startsWith(phoneNumberPrefix)) {
        return {
            kind: "phoneNumber",
            rawId: mri,
            phoneNumber: mri.substring(phoneNumberPrefix.length),
        };
    }
    else if (mri.startsWith(acsUserPrefix) ||
        mri.startsWith(acsGcchUserPrefix) ||
        mri.startsWith(acsDodUserPrefix) ||
        mri.startsWith(spoolUserPrefix)) {
        return { kind: "communicationUser", communicationUserId: mri };
    }
    else {
        return { kind: "unknown", id: mri };
    }
};
const parseJsonString = (str) => {
    if (str === undefined ||
        str === null ||
        str === "" ||
        str === "null" ||
        str === "{}" ||
        str === "[]") {
        return undefined;
    }
    return JSON.parse(str);
};
const createMediaUrlString = (urlString, resourceEndpoint, gatewayApiVersion) => {
    let url;
    try {
        url = new URL(urlString);
        if (url.protocol === "http:" || url.protocol === "https:") {
            // If its already a full url, substitute the origin
            url = new URL(url.pathname, resourceEndpoint);
        }
    }
    catch (_) {
        // urlString is a likely a relative URL, so create a new one with the resourceEndpoint as base
        try {
            url = new URL(urlString, resourceEndpoint);
        }
        catch (_) {
            // If we get here, then the urlString passed in is likely incorrect, so just pass it along
            // As there's nothing we can do at this point.
            return urlString;
        }
    }
    // Append api-version query and return string
    url.searchParams.set("api-version", gatewayApiVersion);
    return url.toString();
};
const isValidURL = (str) => {
    let url;
    try {
        url = new URL(str);
    }
    catch (_) {
        return false;
    }
    return url.protocol === "http:" || url.protocol === "https:";
};
const transformEndpoint = (attachments, resourceEndpoint, gatewayApiVersion) => {
    if (resourceEndpoint === undefined ||
        resourceEndpoint === null ||
        resourceEndpoint === "" ||
        !isValidURL(resourceEndpoint)) {
        return attachments;
    }
    attachments
        .filter((e) => e.attachmentType.toLowerCase() === "image".toLowerCase())
        .map((attachment) => {
        if (attachment.previewUrl) {
            attachment.previewUrl = createMediaUrlString(attachment.previewUrl, resourceEndpoint, gatewayApiVersion);
        }
        if (attachment.url) {
            attachment.url = createMediaUrlString(attachment.url, resourceEndpoint, gatewayApiVersion);
        }
    });
    return attachments;
};
const base64decode = (encodedString) => !core_util_1.isNodeLike ? atob(encodedString) : Buffer.from(encodedString, "base64").toString();
exports.base64decode = base64decode;
const parseJWT = (token) => {
    let [, payload] = token === null || token === void 0 ? void 0 : token.split(".");
    if (payload === undefined) {
        throw new Error("Invalid token");
    }
    payload = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape((0, exports.base64decode)(payload))));
};
const parseTokenCredential = (credential) => __awaiter(void 0, void 0, void 0, function* () {
    const accessToken = yield credential.getToken();
    const jwtToken = accessToken === null || accessToken === void 0 ? void 0 : accessToken.token;
    const parsedJwtToken = parseJWT(jwtToken);
    const identityMri = parsedJwtToken.skypeid;
    const acsResourceId = parsedJwtToken.resourceId;
    const cloudType = getCloudTypeFromSkypeId(identityMri);
    const resourceLocation = parsedJwtToken.resourceLocation || "";
    return { jwtToken, acsResourceId, identityMri, cloudType, resourceLocation };
});
exports.parseTokenCredential = parseTokenCredential;
/**
 * Generated Universally Unique Identifier
 *
 * @returns RFC4122 v4 UUID.
 * @internal
 */
function generateUuid() {
    return (0, uuid_1.v4)();
}
const isEudbLocation = (location) => !!location && !!constants_1.EudbCountries.find((euLocation) => euLocation === location);
exports.isEudbLocation = isEudbLocation;
function getCloudTypeFromSkypeId(skypeId) {
    const cloudPrefix = skypeId.substring(0, skypeId.indexOf(":"));
    switch (cloudPrefix) {
        case constants_1.CloudPrefix.OrgId:
        case constants_1.CloudPrefix.Acs:
        case constants_1.CloudPrefix.Spool: {
            return constants_1.CloudType.Public;
        }
        case constants_1.CloudPrefix.GccHigh:
        case constants_1.CloudPrefix.GccHighAcs: {
            return constants_1.CloudType.GccHigh;
        }
        case constants_1.CloudPrefix.Dod:
        case constants_1.CloudPrefix.DodAcs: {
            return constants_1.CloudType.Dod;
        }
        default: {
            return constants_1.CloudType.Public;
        }
    }
}
